import React, { useEffect, useRef } from "react";
import {
  Auth0Provider,
  Auth0ProviderOptions,
  useAuth0,
} from "@auth0/auth0-react";
import { resetAllSlices, useBoundStore } from "store";
import {
  attachAxiosAuthInterceptors,
  detachAxiosAuthInterceptors,
} from "shared/api/http";
import { router } from "index";
import { isEmpty } from "lodash";
import { useTenant } from "tenants/queries";
import { useTenantUser } from "users/queries";

type Props = { children: React.ReactNode, audience?: string, defaultScope?: string };

const Auth0StateBridge = ({ children, audience, defaultScope }: Props) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, error, user: auth0User, logout } =
    useAuth0();
  const hasNavigatedRef = useRef(false);

  const { setUser, user, setIsAxiosInterceptorsReady, isAxiosInterceptorsReady, setTenant, tenant } = useBoundStore();

  // Fetch tenant information using auth0OrganizationId
  const { data: tenantData } = useTenant(user?.auth0OrganizationId, {
    enabled: !!user?.auth0OrganizationId && isAxiosInterceptorsReady,
  });

  // Fetch tenant user to get role (fallback when not in Auth0 token) and database user ID
  const { data: tenantUser } = useTenantUser(user?.auth0UserId, tenant?.id, {
    enabled: !!user?.auth0UserId && !!tenant?.id && isAxiosInterceptorsReady && (!user?.role || !user?.id),
  });

  // Store tenant in the store when tenant data is fetched
  useEffect(() => {
    if (!tenantData) return;

    setTenant(tenantData);
  }, [tenantData, setTenant]);

  // Update user role and database ID from tenant user data if not already set
  useEffect(() => {
    if (!tenantUser) return;

    const updates: Record<string, any> = {};
    if (tenantUser.role && !user?.role) {
      updates.role = tenantUser.role;
    }
    if (tenantUser.userId && !user?.id) {
      updates.id = tenantUser.userId;
    }
    if (Object.keys(updates).length > 0) {
      setUser(updates);
    }
  }, [tenantUser, user?.role, user?.id, setUser]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasNavigatedRef.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !isEmpty(user?.auth0UserId) || isEmpty(auth0User?.sub)) return;

    setUser({
      auth0UserId: auth0User?.sub,
      role: auth0User?.tenant_roles?.[0] ?? "",
      auth0OrganizationId: auth0User?.org_id,
      email: auth0User?.email ?? "",
      name: auth0User?.name ?? "",
      picture: auth0User?.picture
    });
  }, [isLoading, isAuthenticated, auth0User, user]);

  useEffect(() => {
    if (error) {
      // Expired refresh token or other auth error — clear stale state and force re-login
      resetAllSlices();
      detachAxiosAuthInterceptors();
      logout({ logoutParams: { returnTo: window.location.origin } });
      return;
    }

    if (isLoading) {
      setIsAxiosInterceptorsReady(false);
      detachAxiosAuthInterceptors();
      return;
    }

    if (!isAuthenticated) {
      setIsAxiosInterceptorsReady(false);
      resetAllSlices();
      detachAxiosAuthInterceptors();
      return;
    }

    const tokenParams = audience
      ? { authorizationParams: { audience, scope: defaultScope } }
      : undefined;

    // Validate the token upfront — if the refresh token is missing/expired,
    // force a clean re-login instead of letting every API call fail silently.
    let cancelled = false;
    getAccessTokenSilently(tokenParams).catch(() => {
      if (cancelled) return;
      resetAllSlices();
      detachAxiosAuthInterceptors();
      logout({ logoutParams: { returnTo: window.location.origin } });
    });

    const detach = attachAxiosAuthInterceptors(async () => {
      const token = await getAccessTokenSilently(tokenParams);
      return token;
    });

    setIsAxiosInterceptorsReady(true);

    return () => {
      cancelled = true;
      setIsAxiosInterceptorsReady(false);
      detach();
    };
  }, [audience, defaultScope, error, getAccessTokenSilently, isAuthenticated, isLoading, setIsAxiosInterceptorsReady]);

  return <>{children}</>;
};

export default function Auth0ProviderWithRouter({ children }: Props) {
  const domain = process.env.REACT_APP_AUTH0_DOMAIN!;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID!;
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE!;
  const defaultScope = process.env.REACT_APP_AUTH0_SCOPE ?? "openid profile email";
  const redirectUri = `${window.location.origin}`;

  const authorizationParams: Auth0ProviderOptions["authorizationParams"] = {
    redirect_uri: redirectUri,
    scope: defaultScope,
  };

  if (audience) {
    authorizationParams.audience = audience;
  }

  const onRedirectCallback = (appState?: { returnTo?: string }) => {
    const target =
      appState?.returnTo ||
      `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, document.title, target);
  };

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={authorizationParams}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens
      cacheLocation='localstorage'
    >
      <Auth0StateBridge>{children}</Auth0StateBridge>
    </Auth0Provider>
  );
}
